import apiClient from "./api/client";
import ENDPOINTS from "./api/endpoints";

/**
 * Print Service - handles thermal printing
 *
 * Supports two modes:
 * 1. Print Agent (Client-side) - For SaaS/multi-tenant deployments
 *    - Communicates with local Print Agent running on client machine
 *    - Print Agent handles ESC/POS commands to local printers
 *
 * 2. Server Print (Legacy) - For on-premise single-tenant deployments
 *    - Sends print jobs to backend server
 *    - Server prints to locally connected printers
 */

// Print Agent configuration
// Default port 9191 to avoid conflict with thermal printer raw port (9100)
const DEFAULT_PRINT_AGENT_PORT = 9191;
const PRINT_AGENT_STORAGE_KEY = 'printAgentPort';

// Get configured port from localStorage or use default
const getPrintAgentPort = () => {
  const storedPort = localStorage.getItem(PRINT_AGENT_STORAGE_KEY);
  return storedPort ? parseInt(storedPort, 10) : DEFAULT_PRINT_AGENT_PORT;
};

// Set Print Agent port in localStorage
const setPrintAgentPort = (port) => {
  localStorage.setItem(PRINT_AGENT_STORAGE_KEY, port.toString());
  printAgentAvailable = null; // Reset cache so next check uses new port
};

// Get Print Agent URL dynamically
const getPrintAgentUrl = () => `http://localhost:${getPrintAgentPort()}`;

let printAgentAvailable = null; // Cache availability check

/**
 * Check if Print Agent is running on localhost
 */
const checkPrintAgent = async () => {
  if (printAgentAvailable !== null) {
    return printAgentAvailable;
  }

  try {
    const response = await fetch(`${getPrintAgentUrl()}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(2000) // 2 second timeout
    });
    const data = await response.json();
    printAgentAvailable = data.status === 'running';
    console.log('Print Agent status:', printAgentAvailable ? 'Available' : 'Not available', `(port: ${getPrintAgentPort()})`);
    return printAgentAvailable;
  } catch (error) {
    printAgentAvailable = false;
    console.log('Print Agent not available, using server print mode');
    return false;
  }
};

/**
 * Reset Print Agent availability check (call when switching modes or on error)
 */
const resetPrintAgentCheck = () => {
  printAgentAvailable = null;
};

/**
 * Send request to Print Agent
 */
const sendToPrintAgent = async (endpoint, data) => {
  const response = await fetch(`${getPrintAgentUrl()}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });
  return await response.json();
};

/**
 * Get printers from Print Agent
 */
const getPrintAgentPrinters = async () => {
  const response = await fetch(`${getPrintAgentUrl()}/printers`);
  return await response.json();
};

const printService = {
  // ============================================================
  // Print Agent Methods (Client-side printing)
  // ============================================================

  /**
   * Check if Print Agent is available
   */
  isPrintAgentAvailable: checkPrintAgent,

  /**
   * Reset Print Agent availability cache
   */
  resetPrintAgentCheck,

  /**
   * Get/Set Print Agent port configuration
   */
  getPrintAgentPort,
  setPrintAgentPort,

  /**
   * Get Print Agent status and printers
   */
  getPrintAgentStatus: async () => {
    try {
      const response = await fetch(`${getPrintAgentUrl()}/health`);
      return await response.json();
    } catch (error) {
      return { status: 'not_running', error: error.message };
    }
  },

  /**
   * Get printers configured in Print Agent
   */
  getPrintAgentPrinters: async () => {
    try {
      return await getPrintAgentPrinters();
    } catch (error) {
      console.error('Failed to get Print Agent printers:', error);
      return { success: false, printers: [], error: error.message };
    }
  },

  /**
   * Add/Update printer in Print Agent
   */
  savePrintAgentPrinter: async (printer) => {
    try {
      return await sendToPrintAgent('/printers', printer);
    } catch (error) {
      console.error('Failed to save printer:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete printer from Print Agent
   */
  deletePrintAgentPrinter: async (printerId) => {
    try {
      const response = await fetch(`${getPrintAgentUrl()}/printers/${printerId}`, {
        method: 'DELETE'
      });
      return await response.json();
    } catch (error) {
      console.error('Failed to delete printer:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Test printer via Print Agent
   */
  testPrintAgentPrinter: async (printerId) => {
    try {
      return await sendToPrintAgent('/printers/test', { printerId });
    } catch (error) {
      console.error('Failed to test printer:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Discover printers on network via Print Agent
   */
  discoverPrinters: async () => {
    try {
      const response = await fetch(`${getPrintAgentUrl()}/printers/discover`);
      return await response.json();
    } catch (error) {
      console.error('Failed to discover printers:', error);
      return { success: false, printers: [], error: error.message };
    }
  },

  /**
   * Get Windows printers from Print Agent
   */
  getWindowsPrinters: async () => {
    try {
      const response = await fetch(`${getPrintAgentUrl()}/printers/windows`);
      return await response.json();
    } catch (error) {
      console.error('Failed to get Windows printers:', error);
      return { success: false, printers: [], error: error.message };
    }
  },

  // ============================================================
  // Unified Print Methods (Auto-detect Print Agent or Server)
  // ============================================================

  /**
   * Print a bill receipt
   * @param {Object} printData - Bill print data (includes content, printerId, logo, qrCode)
   * @returns {Promise<Object>} Print response
   */
  printBill: async (printData) => {
    try {
      // Check if Print Agent is available
      const usePrintAgent = await checkPrintAgent();

      if (usePrintAgent) {
        // Use Print Agent (client-side printing)
        return await sendToPrintAgent('/print/bill', {
          content: printData.formattedContent || printData.content,
          printerId: printData.printerId,
          // Include image metadata for Print Agent to handle ESC/POS commands
          logo: printData.logo || null,
          qrCode: printData.qrCode || null,
        });
      } else {
        // Use Server Print (legacy)
        const response = await apiClient.post(ENDPOINTS.PRINT.BILL, printData);
        return response?.data || { success: false, message: 'No response from server' };
      }
    } catch (error) {
      console.error("Failed to print bill:", error);
      throw error;
    }
  },

  /**
   * Print a KOT receipt
   * @param {Object} printData - KOT print data
   * @returns {Promise<Object>} Print response
   */
  printKOT: async (printData) => {
    try {
      const usePrintAgent = await checkPrintAgent();

      if (usePrintAgent) {
        return await sendToPrintAgent('/print/kot', {
          content: printData.formattedContent || printData.content,
          kitchenStationId: printData.kitchenStationId,
          stationName: printData.stationName || printData.kitchenStationName,
          printerId: printData.printerId
        });
      } else {
        const response = await apiClient.post(ENDPOINTS.PRINT.KOT, printData);
        return response?.data || { success: false, message: 'No response from server' };
      }
    } catch (error) {
      console.error("Failed to print KOT:", error);
      throw error;
    }
  },

  /**
   * Print a Cancel KOT receipt
   * @param {Object} printData - Cancel KOT print data
   * @returns {Promise<Object>} Print response
   */
  printCancelKOT: async (printData) => {
    try {
      const usePrintAgent = await checkPrintAgent();

      if (usePrintAgent) {
        return await sendToPrintAgent('/print/kot/cancel', {
          content: printData.formattedContent || printData.content,
          kitchenStationId: printData.kitchenStationId,
          stationName: printData.stationName || printData.kitchenStationName,
          printerId: printData.printerId
        });
      } else {
        const response = await apiClient.post(ENDPOINTS.PRINT.CANCEL_KOT, printData);
        return response?.data || { success: false, message: 'No response from server' };
      }
    } catch (error) {
      console.error("Failed to print Cancel KOT:", error);
      throw error;
    }
  },

  /**
   * Print KOT to a specific station
   * @param {string|number} stationIdOrName - Kitchen station ID or name
   * @param {Object} printData - KOT print data
   * @returns {Promise<Object>} Print response
   */
  printKOTToStation: async (stationIdOrName, printData) => {
    try {
      const usePrintAgent = await checkPrintAgent();

      if (usePrintAgent) {
        // Support both station ID (number) and station name (string)
        const isId = typeof stationIdOrName === 'number';
        return await sendToPrintAgent('/print/kot', {
          content: printData.formattedContent || printData.content,
          kitchenStationId: isId ? stationIdOrName : printData.kitchenStationId,
          stationName: isId ? printData.stationName : stationIdOrName,
          printerId: printData.printerId
        });
      } else {
        const response = await apiClient.post(
          ENDPOINTS.PRINT.KOT_TO_STATION(stationIdOrName),
          printData
        );
        return response?.data || { success: false, message: 'No response from server' };
      }
    } catch (error) {
      console.error(`Failed to print KOT to station ${stationIdOrName}:`, error);
      throw error;
    }
  },

  /**
   * Test printer connection
   * @param {string} printerType - 'BILL' or 'KOT'
   * @returns {Promise<Object>} Test result
   */
  testPrint: async (printerType) => {
    try {
      const usePrintAgent = await checkPrintAgent();

      if (usePrintAgent) {
        // Get default printer for type from Print Agent
        const response = await fetch(`${getPrintAgentUrl()}/printers/default/${printerType}`);
        const data = await response.json();
        if (data.success && data.printer) {
          return await sendToPrintAgent('/printers/test', { printerId: data.printer.id });
        }
        return { success: false, error: `No default ${printerType} printer configured` };
      } else {
        const response = await apiClient.post(
          `${ENDPOINTS.PRINT.TEST}?printerType=${printerType}`
        );
        return response?.data || { success: false, message: 'No response from server' };
      }
    } catch (error) {
      console.error("Failed to test print:", error);
      throw error;
    }
  },

  /**
   * Test print with pre-formatted content from UI
   * @param {string} formattedContent - The pre-formatted receipt text
   * @param {string} receiptType - BILL, KOT, or CANCEL_KOT
   * @param {number} printerId - Optional specific printer ID to use
   * @param {Object} metadata - Optional metadata for images (logo, qrCode)
   * @returns {Promise<Object>} Test result
   */
  testPrintTemplate: async (formattedContent, receiptType, printerId = null, metadata = null) => {
    try {
      const usePrintAgent = await checkPrintAgent();

      if (usePrintAgent) {
        const endpoint = receiptType === 'BILL' ? '/print/bill' :
                        receiptType === 'CANCEL_KOT' ? '/print/kot/cancel' : '/print/kot';
        return await sendToPrintAgent(endpoint, {
          content: formattedContent,
          printerId: printerId,
          // Include image metadata for Print Agent to handle ESC/POS commands
          logo: metadata?.logo || null,
          qrCode: metadata?.qrCode || null,
        });
      } else {
        const response = await apiClient.post(
          ENDPOINTS.PRINT.TEST_TEMPLATE,
          {
            formattedContent,
            receiptType,
            printerId,
            // Include image metadata for server-side printing
            logo: metadata?.logo || null,
            qrCode: metadata?.qrCode || null,
          }
        );
        return response || { success: false, message: 'No response from server' };
      }
    } catch (error) {
      console.error("Failed to test print template:", error);
      throw error;
    }
  },

  // ============================================================
  // Helper functions to format data for printing
  // ============================================================

  /**
   * Format bill data for print request
   */
  formatBillPrintData: ({
    receiptNumber,
    tableName,
    tableType,
    tableSplit,
    pax,
    items,
    subtotal,
    discountAmount,
    discountPercentage,
    discountType,
    taxAmount,
    taxPercentage,
    roundOff,
    finalAmount,
    paymentMode,
    cashReceived,
    changeDue,
    upiId,
    cashPart,
    onlinePart,
  }) => {
    return {
      printType: 'BILL',
      content: {
        receiptNumber,
        tableName,
        tableType,
        tableSplit,
        pax,
        timestamp: new Date().toLocaleString('en-IN'),
        items: items?.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.total || (item.price * item.quantity),
          variantName: item.variantName,
        })),
        subtotal,
        discountAmount,
        discountPercentage,
        discountType,
        taxAmount,
        taxPercentage,
        roundOff,
        finalAmount,
        paymentMode,
        cashReceived,
        changeDue,
        upiId,
        cashPart,
        onlinePart,
      }
    };
  },

  /**
   * Format KOT data for print request
   */
  formatKOTPrintData: ({
    kotNumber,
    tableName,
    tableSplit,
    pax,
    stationName,
    items,
    chefNotes,
    allergies,
  }) => {
    return {
      printType: 'KOT',
      kitchenStationName: stationName,
      content: {
        kotNumber,
        tableName,
        tableSplit,
        pax,
        stationName,
        timestamp: new Date().toLocaleString('en-IN'),
        items: items?.map(item => ({
          name: item.name || item.itemName,
          quantity: item.quantity,
          customization: item.customization ? {
            size: item.customization.size,
            spiceLevel: item.customization.spiceLevel,
            preparation: item.customization.preparation,
            quickNotes: item.customization.quickNotes,
            customInstructions: item.customization.customInstructions,
          } : null,
        })),
        chefNotes,
        allergies,
        isCancellation: false,
      }
    };
  },

  /**
   * Format Cancel KOT data for print request
   */
  formatCancelKOTPrintData: ({
    kotNumber,
    tableName,
    tableSplit,
    pax,
    stationName,
    items,
    cancelReason,
  }) => {
    return {
      printType: 'CANCEL_KOT',
      kitchenStationName: stationName,
      content: {
        kotNumber,
        tableName,
        tableSplit,
        pax,
        stationName,
        timestamp: new Date().toLocaleString('en-IN'),
        items: items?.map(item => ({
          name: item.name || item.itemName,
          quantity: item.quantity,
          customization: item.customization ? {
            size: item.customization.size,
            spiceLevel: item.customization.spiceLevel,
            preparation: item.customization.preparation,
            quickNotes: item.customization.quickNotes,
            customInstructions: item.customization.customInstructions,
          } : null,
        })),
        isCancellation: true,
        cancelReason,
      }
    };
  },
};

export default printService;
